import { DateFormatString } from "../DatePicker/DateFormatters";

// Pure helpers for the Digital Twin "Components" tab (Battery sub-tab).
// Kept free of React so the grouping / badge logic can be unit-tested in
// isolation (see CLAUDE.md "test pure logic" convention).

// The asset noun is not hardcoded: this feature ships to orgs that call the thing a
// vehicle rather than a bus, and the same file already switches on that for the tab
// label (getComponentsTabLabel). Defaults keep the wording unchanged for bus orgs.
const buildReplaceBlockedTooltip = (assetLabel = "bus", partLabel = "battery pack") =>
    `Change the ${assetLabel} status to "Non-operational" to replace this ${partLabel}.`;

// Whether the replace controls appear at all.
//
// The two gates are answered differently on purpose. Being operational is a state
// the user can change, so that control stays on screen and says how to open it. A
// missing permission is not: no amount of clicking will help, so a permanently
// dead button is noise that only advertises something they cannot have.
const isReplaceVisible = ({ canEdit = true } = {}) => !!canEdit;

// Why replacing is unavailable while the control is still SHOWN, or null when it
// is available. Both the per-card Replace button and the header's "Replace
// Multiple" button read this, so they can never explain one closed gate two
// different ways.
//
// The permission gate is deliberately absent: it hides the control instead (see
// isReplaceVisible), so there is nothing left to explain. Callers must ask
// canReplacePacks, not `getReplaceBlockedReason(...) === null`, to know whether
// replacing is actually allowed.
const getReplaceBlockedReason = ({
    isNonOperational = false,
    assetLabel,
    partLabel,
} = {}) =>
    isNonOperational ? null : buildReplaceBlockedTooltip(assetLabel, partLabel);

// Whether the Edit Pack Details control appears.
//
// Same hide-don't-disable stance as isReplaceVisible: a missing permission is not a
// state the user can change, so a permanently dead button would only advertise
// something they cannot have.
//
// Deliberately NOT gated on the bus being Non-operational, unlike Replace. Correcting
// a mistyped serial is a data fix, not a physical act on a vehicle, and requiring a
// status change to reach it would push users into making a real one.
//
// absPackNo is required because the endpoint is keyed on it: a count-only slot has no
// packConfigID, and that is exactly the slot most likely to need its serial filled in.
const isEditPackVisible = ({ canEditDetails = false, pack } = {}) =>
    !!canEditDetails && pack?.absPackNo != null;

// Recency chip derived from a pack's installedOn date.
// Returns one of the Figma labels, or null when the pack was not replaced
// recently (older than ~2 weeks) or has no install date.
const getPackRecencyBadge = (installedOn) => {
    if (!installedOn) return null;

    // Parse only the calendar day (YYYY-MM-DD) as a LOCAL date so a date-only
    // value is not shifted by a UTC-midnight parse. Callers pass a value already
    // localized to the user's timezone.
    const [y, m, d] = String(installedOn).slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    const installed = new Date(y, m - 1, d);
    if (isNaN(installed.getTime())) return null;

    const now = new Date();
    const startOfInstalled = new Date(
        installed.getFullYear(),
        installed.getMonth(),
        installed.getDate()
    );
    const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
    );

    const diffDays = Math.round((startOfToday - startOfInstalled) / 86400000);

    if (diffDays < 0) return null; // future install date — treat as no badge
    if (diffDays === 0) return "Replaced Today";
    if (diffDays === 1) return "Replaced Yesterday";
    if (diffDays <= 6) return "Replaced This Week";
    if (diffDays <= 13) return "Replaced 1 Week Ago";
    return null;
};

// Human date for the "Last replaced date" line. "-" when never replaced.
const formatReplacedDate = (installedOn) => {
    if (!installedOn) return "-";
    return DateFormatString(installedOn) || "-";
};

// Collect every replaceable (pack) node beneath a node, regardless of how
// many intermediate levels (module, etc.) sit between string and pack.
const collectReplaceablePacks = (node) => {
    let packs = [];
    const children = node?.children || [];
    for (const child of children) {
        if (child?.info?.replaceable) {
            packs.push(child);
        } else {
            packs = packs.concat(collectReplaceablePacks(child));
        }
    }
    return packs;
};

// A transformed hierarchy pack node -> the shape PackCard consumes.
// node.id === systemID === packConfigID for pack nodes (see
// transformTwinHierarchyData + backend contract §3.1).
const mapPackNode = (node) => ({
    id: node.id,
    packConfigID: node.id,
    // Stable slot position: drives the "Pack N" label (continuous across strings)
    // and groups the slot's replacement history, unlike packConfigID which changes
    // on every replacement.
    absPackNo: node?.info?.absPackNo ?? null,
    label:
        node?.info?.absPackNo != null ? `Pack ${node.info.absPackNo}` : node.label,
    // Always the real customerPackID (backend info.currentSerial); never fall
    // back to displayName/synthetic id. Empty => no serial on record.
    serial: node?.info?.currentSerial ?? "",
    installedOn: node?.info?.installedOn ?? null,
    // When the current pack was installed via a replacement (drives the recency
    // badge + "Last replaced date"); null for never-replaced packs.
    lastReplacedAt: node?.info?.lastReplacedAt ?? null,
    manufacturingDate: node?.info?.manufacturingDate ?? null,
    hasReplacementHistory: !!node?.info?.hasReplacementHistory,
    sohBms: node?.info?.sohBms ?? null,
});

// Take the transformed hierarchy tree (output of transformTwinHierarchyData)
// and return the strings -> packs structure the grid renders:
// [{ id, label, sohBms, packs: [...] }]
const extractStringsWithPacks = (tree = []) => {
    const strings = [];

    const walk = (nodes) => {
        for (const node of nodes) {
            const level = (node?.level || "").toLowerCase();
            if (level === "string") {
                strings.push({
                    id: node.id,
                    label: node.label, // "String 1"
                    sohBms: node?.info?.sohBms ?? null,
                    packs: collectReplaceablePacks(node).map(mapPackNode),
                });
            } else if (node?.children?.length) {
                walk(node.children);
            }
        }
    };

    walk(tree);
    return strings;
};

// Format a BMS SoH value (number or numeric string) as "98%". null -> "-".
const formatBmsSoh = (sohBms) => {
    if (sohBms === null || sohBms === undefined || sohBms === "") return "-";
    const num = parseFloat(sohBms);
    if (isNaN(num)) return "-";
    return `${Math.round(num)}%`;
};

// Flatten service records into per-pack-replacement Event History entries.
// packConfigID null => battery-level (all packs). Sorted newest first.
// Group a slot's replacement history by its stable absPackNo, NOT packConfigID:
// under the append-only model every replacement mints a new packConfigID, so
// filtering by the current pack's id would only surface the latest swap.
const buildEventHistoryEntries = (records = [], absPackNo = null) => {
    const entries = [];

    for (const rec of records || []) {
        const sd = rec?.serviceDetails || {};
        for (const p of rec?.packReplacementDetails || []) {
            if (
                absPackNo !== null &&
                absPackNo !== undefined &&
                String(p.absPackNo) !== String(absPackNo)
            ) {
                continue;
            }
            entries.push({
                id: `${rec.id}-${p.absPackNo}-${p.incomingPackConfigID}`,
                recordedAt: sd.recordedAt || null,
                actorName: sd.recordedByName || null,
                packLabel: p.pack || null,
                stringLabel: p.string || null,
                oldSerial: p.outgoingSerial || null,
                newSerial: p.incomingSerial || null,
                reasons: (p.reasons || [])
                    .map((r) => (typeof r === "string" ? r : r?.label || r?.code))
                    .filter(Boolean),
                remark: sd.remarks || null,
            });
        }
    }

    entries.sort((a, b) => {
        const ta = a.recordedAt ? new Date(a.recordedAt).getTime() : 0;
        const tb = b.recordedAt ? new Date(b.recordedAt).getTime() : 0;
        return tb - ta;
    });

    return entries;
};

// Two-way cross-check for the operational-status "replaced during downtime?"
// answer against the recorded replacement history. Returns an error key, or
// null when the answer is consistent.
//   'required'    - no answer chosen
//   'yesButNone'  - said Yes but no replacement recorded since last non-op
//   'noButExists' - said No but a replacement exists since last non-op
const validateBatteryReplacementAnswer = ({ answer, hasReplacementSince }) => {
    if (answer !== "yes" && answer !== "no") return "required";
    if (answer === "yes" && !hasReplacementSince) return "yesButNone";
    if (answer === "no" && hasReplacementSince) return "noButExists";
    return null;
};

// ---------------------------------------------------------------------------
// Multi-pack selection (Battery sub-tab "Replace Multiple" mode)
// ---------------------------------------------------------------------------

// Whether replacement is allowed at all on this bus, for this user. Shared by
// the per-card Replace button, the "Replace Multiple" entry point and the
// selection checkboxes so the three cannot disagree about when replacing is
// permitted.
const canReplacePacks = (gate = {}) =>
    isReplaceVisible(gate) && getReplaceBlockedReason(gate) === null;

// Whether one specific pack can be picked for replacement. Beyond the bus-level
// gate above, a pack needs an absPackNo: that stable slot number is what is
// handed to the Record Replacement form (and what groups the slot's history),
// so a node without one cannot be targeted.
const isPackSelectable = (pack, gate = {}) =>
    canReplacePacks(gate) && pack?.absPackNo !== null && pack?.absPackNo !== undefined;

// The two things selection mode can be started FOR. The grid, the action bar and the
// checkboxes all read the same value, so they cannot disagree about which action the
// current selection is being gathered for.
const SELECTION_MODE_REPLACE = "replace";
const SELECTION_MODE_EDIT = "edit";

// Whether one specific pack can be picked for a DETAILS EDIT.
//
// Deliberately not canReplacePacks: editing is gated on BATTERY_COMPONENTS update
// rather than create, and is NOT gated on the bus being Non-operational. Correcting a
// mistyped serial is a data fix, not a physical act on a vehicle.
//
// A pack with no serial on record is selectable on purpose -- filling one in is the
// case this feature exists for.
const isPackEditSelectable = (pack, gate = {}) =>
    isEditPackVisible({ ...gate, pack });

// Which selectability rule applies to the mode currently active.
const isPackSelectableForMode = (pack, mode, gate = {}) =>
    mode === SELECTION_MODE_EDIT
        ? isPackEditSelectable(pack, gate)
        : isPackSelectable(pack, gate);

// Walk the rendered strings -> packs structure and return the selected packs in
// SLOT order (String 1 Pack 1, Pack 2, ...), each carrying its string's label.
//
// Deliberately derived from the render order rather than from the order the user
// happened to tick the boxes: the Record Replacement form lists one card per
// pack, and those cards should read in slot order however the selection was made.
const collectSelectedPacks = (strings = [], selectedAbsPackNos = []) => {
    const selected = new Set((selectedAbsPackNos || []).map((no) => String(no)));
    const packs = [];
    (strings || []).forEach((string) => {
        (string?.packs || []).forEach((pack) => {
            if (selected.has(String(pack?.absPackNo))) {
                packs.push({ ...pack, stringLabel: string?.label || "" });
            }
        });
    });
    return packs;
};

// Sticky-bar count line. Singular at exactly one, so it doesn't read "1 packs".
const formatSelectionCount = (count = 0) =>
    `${count} ${count === 1 ? "pack" : "packs"} selected`;

// Copy for whichever selection mode is running. Kept together so the chip and the
// bar's primary button stay consistent as a set.
const getSelectionModeCopy = (mode) =>
    mode === SELECTION_MODE_EDIT
        ? {
              chip: "Selection mode — pick packs to edit",
              continueLabel: "Continue to Edit",
              testId: "selection-continue-edit",
          }
        : {
              chip: "Selection mode — pick packs to replace",
              continueLabel: "Continue to Replacement",
              testId: "selection-continue",
          };

// Build the flat query-param object for /record-replacement.
//
// Params round-trip through URLSearchParams (encryptTheParams -> decryptTheParams),
// which flattens everything to strings — an array of pack objects cannot survive
// the trip. So only the slot numbers travel, as a comma list, and the form
// re-resolves label/serial/packConfigID from the components tree. That also keeps
// the form's serials fresh: a serial captured here can already be retired by the
// time the form is submitted, which is what makes a re-replace 409.
const buildPackSelectionParams = ({
    batteryID,
    packs = [],
    lastNonOperationalAt = "",
    vehicleNumber = "",
    pageType = "",
} = {}) => {
    return {
        batteryID,
        absPackNos: packs.map((p) => p.absPackNo).join(","),
        // Pre-fills the "Issue reported date time" field (still editable).
        lastNonOperationalAt: lastNonOperationalAt || "",
        vehicleNumber: vehicleNumber || "",
        pageType: pageType || "",
    };
};

// Label for the Components tab and its page heading. The tab holds this asset's packs
// and replacement logs, so the noun follows the asset: "Bus Components" for a bus
// application, "Battery Components" everywhere else.
//
// showBusDeviceInfo is the existing battery-vs-bus switch (DigitalTwin.jsx, derived from
// batteryConfig.application) already used to pick BusDeviceInfo over DefaultInfo in the
// header. There is no shared asset-noun token in the lang files - only bus-specific keys
// like bus_id - so the choice is made here and both call sites read it, rather than each
// hardcoding a noun and drifting apart.
//
// vehicleLabel is the org's globalConfig.baseEntity.label (e.g. "Vehicle"): when set, the
// vehicle variant reads "<label> Components" instead of the bus wording.
const getComponentsTabLabel = (isBusApplication, t, vehicleLabel = null) => {
    const translate = typeof t === "function" ? t : (_key, fallback) => fallback;
    if (!isBusApplication) return translate("digital_twin:battery_components", "Battery Components");
    return vehicleLabel
        ? `${vehicleLabel} Components`
        : translate("digital_twin:bus_components", "Bus Components");
};

export {
    getComponentsTabLabel,
    isEditPackVisible,
    isPackEditSelectable,
    isPackSelectableForMode,
    getSelectionModeCopy,
    SELECTION_MODE_REPLACE,
    SELECTION_MODE_EDIT,
    canReplacePacks,
    isReplaceVisible,
    isPackSelectable,
    collectSelectedPacks,
    formatSelectionCount,
    buildPackSelectionParams,
    getPackRecencyBadge,
    buildEventHistoryEntries,
    validateBatteryReplacementAnswer,
    formatReplacedDate,
    extractStringsWithPacks,
    collectReplaceablePacks,
    mapPackNode,
    formatBmsSoh,
    getReplaceBlockedReason,
    buildReplaceBlockedTooltip,
};
